import { zodResolver } from '@hookform/resolvers/zod';
import { type Category, type CategoryKind, categoryNameSchema } from '@familywallet/shared';
import { useMutation } from '@tanstack/react-query';
import { type ComponentProps, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { FormError } from '../components/FormError';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Segmented } from '../components/ui/segmented';
import { Field } from '../components/ui/field';
import { Input, Select } from '../components/ui/input';
import { api } from '../lib/api';
import { useCan } from '../lib/auth';
import { useCategories } from '../lib/ledger';
import { useInvalidateLedger } from '../lib/ledger-mutations';
import { useIdempotencyKey } from '../lib/members';
import { cn } from '../lib/utils';

const nameForm = z.object({
  name: categoryNameSchema,
  // '' = top level (a <select> value).
  parentId: z.string(),
});
type NameInput = z.input<typeof nameForm>;
type NameOutput = z.output<typeof nameForm>;

/** The parent picker: top level, or an active top-level category of the same kind. */
function ParentSelect({
  parents,
  ...props
}: { parents: readonly Category[] } & ComponentProps<'select'>) {
  const { t } = useTranslation();
  return (
    <Select {...props}>
      <option value="">{t('categories.topLevel')}</option>
      {parents.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </Select>
  );
}

function AddCategory({ kind, parents }: { kind: CategoryKind; parents: readonly Category[] }) {
  const { t } = useTranslation();
  const invalidate = useInvalidateLedger();
  const [idempotencyKey, renewKey] = useIdempotencyKey();
  const form = useForm<NameInput, unknown, NameOutput>({
    resolver: zodResolver(nameForm),
    defaultValues: { name: '', parentId: '' },
  });
  const add = useMutation({
    mutationFn: (v: NameOutput) =>
      api.categories.create(
        { name: v.name, kind, parentId: v.parentId || null },
        { idempotencyKey },
      ),
    onSuccess: async () => {
      renewKey();
      form.reset({ name: '', parentId: '' });
      await invalidate();
    },
  });
  return (
    <Card>
      <form
        noValidate
        className="flex flex-col gap-3"
        onSubmit={form.handleSubmit((v) => add.mutate(v))}
      >
        <FormError error={add.error} />
        <Field label={t('categories.newName')} error={form.formState.errors.name}>
          <Input {...form.register('name')} />
        </Field>
        {parents.length > 0 && (
          <Field label={t('categories.parent')}>
            <ParentSelect parents={parents} {...form.register('parentId')} />
          </Field>
        )}
        <Button type="submit" disabled={add.isPending}>
          {t('categories.add')}
        </Button>
      </form>
    </Card>
  );
}

function CategoryRow({
  category,
  canManage,
  parents,
  parentName,
  hasChildren,
}: {
  category: Category;
  canManage: boolean;
  parents: readonly Category[];
  parentName: string | null;
  hasChildren: boolean;
}) {
  const { t } = useTranslation();
  const invalidate = useInvalidateLedger();
  const [editing, setEditing] = useState(false);
  const form = useForm<NameInput, unknown, NameOutput>({
    resolver: zodResolver(nameForm),
    values: { name: category.name, parentId: category.parentId ?? '' },
  });
  const rename = useMutation({
    mutationFn: (v: NameOutput) =>
      api.categories.rename(category.id, { name: v.name, parentId: v.parentId || null }),
    onSuccess: async () => {
      setEditing(false);
      await invalidate();
    },
  });
  const archive = useMutation({
    mutationFn: () =>
      category.archivedAt
        ? api.categories.unarchive(category.id)
        : api.categories.archive(category.id),
    onSuccess: invalidate,
  });

  if (editing) {
    return (
      <li className="px-4 py-3">
        <form
          noValidate
          className="flex flex-col gap-2"
          onSubmit={form.handleSubmit((v) => rename.mutate(v))}
        >
          <FormError error={rename.error} />
          <Field label={t('categories.rename')} error={form.formState.errors.name}>
            <Input autoFocus {...form.register('name')} />
          </Field>
          {!hasChildren && (
            <Field label={t('categories.parent')}>
              <ParentSelect
                parents={parents.filter((p) => p.id !== category.id)}
                {...form.register('parentId')}
              />
            </Field>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="small" disabled={rename.isPending}>
              {t('common.save')}
            </Button>
            <Button variant="ghost" onClick={() => setEditing(false)}>
              {t('common.cancel')}
            </Button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li
      className={cn(
        'flex min-h-16 flex-wrap items-center justify-between gap-2 px-4 py-2',
        parentName !== null && 'ps-8',
      )}
    >
      <span className="flex flex-col">
        <span className={cn('font-medium', category.archivedAt && 'text-muted line-through')}>
          {category.name}
        </span>
        <span className="text-xs text-muted">
          {parentName !== null && `${t('categories.under', { name: parentName })} · `}
          {category.archivedAt
            ? t('categories.archived')
            : t('categories.used', { count: category.usageCount })}
        </span>
      </span>
      {canManage && (
        <span className="flex gap-1">
          <FormError error={archive.error} />
          {!category.archivedAt && (
            <Button variant="ghost" onClick={() => setEditing(true)}>
              {t('categories.rename')}
            </Button>
          )}
          <Button variant="ghost" onClick={() => archive.mutate()} disabled={archive.isPending}>
            {category.archivedAt ? t('categories.unarchive') : t('categories.archive')}
          </Button>
        </span>
      )}
    </li>
  );
}

export function CategoriesPage() {
  const { t } = useTranslation();
  const canManage = useCan('categories.manage');
  const [kind, setKind] = useState<CategoryKind>('expense');
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState('');
  const categories = useCategories(kind, showArchived);

  const needle = search.trim().toLocaleLowerCase();
  const all = categories.data?.items ?? [];
  const nameOf = new Map(all.map((c) => [c.id, c.name]));
  const parents = all.filter((c) => c.parentId === null && c.archivedAt === null);
  const childrenOf = (id: string) => all.filter((c) => c.parentId === id);
  // Each subcategory right under its parent (a parent not listed: at the end).
  const ordered = [
    ...all.filter((c) => c.parentId === null).flatMap((c) => [c, ...childrenOf(c.id)]),
    ...all.filter((c) => c.parentId !== null && !all.some((p) => p.id === c.parentId)),
  ];
  const items = ordered.filter((c) => c.name.toLocaleLowerCase().includes(needle));

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={t('categories.title')} back="/family" />
      <Segmented
        label={t('categories.title')}
        options={(['expense', 'income'] as const).map((k) => ({
          value: k,
          label: t(`categories.${k}`),
        }))}
        value={kind}
        onChange={setKind}
      />
      {!canManage && <p className="text-sm text-muted">{t('categories.readOnly')}</p>}
      <Field label={t('categories.search')}>
        <Input type="search" value={search} onChange={(e) => setSearch(e.target.value)} />
      </Field>
      {categories.isError && <FormError error={categories.error} />}
      <Card className="overflow-hidden p-0">
        {categories.isPending ? (
          <p role="status" className="p-4">
            {t('app.loading')}
          </p>
        ) : items.length === 0 ? (
          <p className="p-4 text-muted">{t('categories.none')}</p>
        ) : (
          <ul className="flex flex-col divide-y divide-line">
            {items.map((c) => (
              <CategoryRow
                key={c.id}
                category={c}
                canManage={canManage}
                parents={parents}
                parentName={c.parentId ? (nameOf.get(c.parentId) ?? '') : null}
                hasChildren={childrenOf(c.id).length > 0}
              />
            ))}
          </ul>
        )}
      </Card>
      {canManage && <AddCategory key={kind} kind={kind} parents={parents} />}
      <Button variant="ghost" onClick={() => setShowArchived((v) => !v)}>
        {t('categories.showArchived')}
        {showArchived ? ' ✓' : ''}
      </Button>
    </div>
  );
}
